// Third-party
import React from 'react';
import { withFormik, FormikProps, Form, Field } from 'formik';
import * as Yup from 'yup';
import { FormInput } from '../forms';

// Utils
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import { SHARED_CONTENT_METHOD } from '../../../utils/constants';
import { capitalizeFirst } from '../../../utils/strings';

// Components
import { PrimaryButton, CancelButton } from '../buttons';
import SharedComparisonInfos from '@/types/shared-comparison-infos';
import Nullable from '@/types/nullable';
import Modal from '@/views/components/modals/AccessibleModal';
import { Typography } from '@capintel/component-lib/typography';
import { Stack } from '@capintel/component-lib/stack';

interface ParentProps {
  onSubmit: (info: SharedComparisonInfos) => Promise<Nullable<string>>;
  onCancel: () => void;
  onSuccess: (url: Nullable<string>) => void;
  showModal: boolean;
  type: string;
  shareMethod: SHARED_CONTENT_METHOD;
}

type Props = ParentProps & FormikProps<SharedComparisonInfos>;

const canSubmit = (props: Props) => {
  const { values, initialValues, isSubmitting, errors } = props;

  const isDirty = values !== initialValues;
  const hasErrors = Object.keys(errors).length > 0;

  return !hasErrors && isDirty && !isSubmitting;
};

const renderSubmitButton = (props: Props): JSX.Element => {
  const { isSubmitting, shareMethod } = props;
  switch (shareMethod) {
    case SHARED_CONTENT_METHOD.EMAIL: {
      return (
        <PrimaryButton className="submit-button" type="submit" disabled={!canSubmit(props)} loading={isSubmitting}>
          <FormattedMessage id="ShareContentModal.SendEmail" />
        </PrimaryButton>
      );
    }
    case SHARED_CONTENT_METHOD.LINK: {
      return (
        <PrimaryButton className="submit-button" type="submit" disabled={!canSubmit(props)} loading={isSubmitting}>
          <FormattedMessage id="ShareContentModal.GenerateLink" />
        </PrimaryButton>
      );
    }
    default: {
      return <></>;
    }
  }
};

const ShareContentModal = (props: Props) => {
  const onCancel = () => {
    props.resetForm();
    props.onCancel();
  };
  const { showModal, shareMethod, isSubmitting } = props;

  return (
    <Modal show={showModal} className="share-content invite-modal" onHide={onCancel} dialogClassName="modal-dialog-centered">
      <Form>
        <Modal.Header className="d-flex flex-column align-items-center">
          <Stack gap="400">
            <Typography variant="headerMd" as="h1">
              {formatMessage(`ShareContentModal.Title.${capitalizeFirst(shareMethod)}`)}
            </Typography>
            <Typography variant="bodyMdRegular" as="p">
              {formatMessage(`ShareContentModal.FormText.${capitalizeFirst(shareMethod)}`)}
            </Typography>
          </Stack>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column">
          <div className="fields d-flex justify-content-between">
            <FormInput name="fname" label={formatMessage('Onboarding.Fields.fname')} required data-private data-dd-privacy="mask" />
            <FormInput name="lname" label={formatMessage('Onboarding.Fields.lname')} required data-private data-dd-privacy="mask" />
          </div>
          <div className="fields d-flex justify-content-between">
            <FormInput name="email" label={formatMessage('Onboarding.Fields.email')} required data-private data-dd-privacy="mask" />
            <FormInput name="company" label={formatMessage('Onboarding.Fields.company')} required data-private data-dd-privacy="mask" />
          </div>
          {shareMethod === SHARED_CONTENT_METHOD.EMAIL && (
            <>
              <div className="invite-people-modal-labels d-flex message-label">
                <label>
                  <FormattedMessage id="ShareContentModal.Message" />
                </label>
              </div>
              <Field
                maxLength="1000"
                spellCheck="false"
                name="message"
                component="textarea"
                placeholder={formatMessage('ShareContentModal.MessagePlaceholder')}
                data-private
                data-dd-privacy="mask"
              />
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex flex-column align-items-center">
          {renderSubmitButton(props)}
          <CancelButton onClick={onCancel} disabled={isSubmitting} />
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const ShareContentSchema = Yup.object().shape({
  fname: Yup.string().required(),
  lname: Yup.string().required(),
  company: Yup.string().required(),
  email: Yup.string().email().required(),
});

const formikWrapper = withFormik<Props, SharedComparisonInfos>({
  validationSchema: ShareContentSchema,
  enableReinitialize: true,
  mapPropsToValues: ({ shareMethod, type }: Props) => ({ email: '', fname: '', lname: '', company: '', shareMethod, type }),
  handleSubmit: (values, { props: { onSubmit, onSuccess }, resetForm, setSubmitting }) => {
    onSubmit(values)
      .then(url => {
        resetForm();
        onSuccess(url);
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
});

export default formikWrapper(ShareContentModal) as any;
